import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddUpdateModal = ({ project, onClose, onSave }) => {
  const { token, user } = useAuth();
  const [updateText, setUpdateText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!updateText.trim()) {
      alert('Please enter update text');
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Create a new update record in project_updates collection
      await axios.post(`${API}/projects/${project.id}/updates`, {
        text: updateText,
        date: new Date().toISOString()
      }, { headers });
      
      // Also update the project's field_247 (summary/update field) for backward compatibility
      await axios.put(`${API}/projects/${project.id}`, {
        field_247: updateText
      }, { headers });
      
      // Dispatch custom event to notify other components about the new update
      window.dispatchEvent(new CustomEvent('projectUpdateCreated', {
        detail: { projectId: project.id, text: updateText }
      }));
      
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Failed to add update:', error);
      alert('Failed to add update. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Add Update to Project
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Project: <span className="font-medium text-gray-900 dark:text-white">{project.field_5_raw || 'Untitled'}</span>
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Update Text *
          </label>
          <textarea
            value={updateText}
            onChange={(e) => setUpdateText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            rows="6"
            placeholder="Enter project update..."
            required
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !updateText.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Add Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUpdateModal;
